import React, { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { userService } from '../../_services';

const ReferralPopup = ({ closePopup, whName }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [number, setNumber] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            return data;
        });
    }, []);

    const postReferral = useCallback((whname, referBy, firstName, lastName, emailId, mobileNo, wholesalerId) => {
        setLoading(true);
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                whname,
                refer_by: referBy,
                first_name: firstName,
                last_name: lastName,
                email_id: emailId,
                mobile_no: mobileNo,
                wholesaler_Id: wholesalerId
            })
        };

        fetch(`/api/ds/disrefer`, requestOptions)
            .then(handleResponse)
            .then(user => {
                setLoading(false);
                if (user && user.length > 0) {
                    setError(user[0].status_message);
                    if (user[0].status_code === "1" || user[0].status_message.toLowerCase().includes('success')) {
                        setSuccess(true);
                        // Clear form on success
                        setTimeout(() => {
                            closePopup();
                        }, 2000);
                    }
                } else {
                    setError('Whoops, something went wrong. Please try again');
                }
            })
            .catch(() => {
                setLoading(false);
                setError('Whoops, something went wrong. Please try again');
            });
    }, [handleResponse, closePopup]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        // Validation
        if (!firstName || !lastName || !number || !email) {
            setError('All fields are required');
            return;
        }

        if (!userService.ValidateEmail(email)) {
            setError('Enter a valid email ID');
            return;
        }

        if (userdata) {
            postReferral(
                userdata.wholesaler,
                userdata.paramsname,
                firstName,
                lastName,
                email,
                number,
                userdata.wholesaler_Id
            );
        }
    }, [firstName, lastName, number, email, userdata, postReferral]);

    return (
        <Dialog open={true} onOpenChange={closePopup}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Referral</DialogTitle>
                    <DialogDescription>
                        Our team at {whName} will reach out to this rep candidate on your behalf
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center items-center py-4">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : 

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="firstName">First Name</Label>
                        <Input
                            type="text"
                            id="firstName"
                            name="firstName"
                            placeholder="John"
                            value={firstName}
                            onChange={(e) => {
                                setFirstName(e.target.value);
                                setError('');
                            }}
                            className={error && !firstName ? 'border-destructive' : ''}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                            type="text"
                            id="lastName"
                            name="lastName"
                            placeholder="Smith"
                            value={lastName}
                            onChange={(e) => {
                                setLastName(e.target.value);
                                setError('');
                            }}
                            className={error && !lastName ? 'border-destructive' : ''}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="number">Contact Number</Label>
                        <Input
                            type="text"
                            id="number"
                            name="number"
                            placeholder="555-123-7890"
                            value={number}
                            onChange={(e) => {
                                setNumber(e.target.value);
                                setError('');
                            }}
                            className={error && !number ? 'border-destructive' : ''}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            type="email"
                            id="email"
                            name="email"
                            placeholder="john@smith.com"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setError('');
                            }}
                            className={error && !email ? 'border-destructive' : ''}
                        />
                    </div>

                    {error && (
                        <div className={`text-sm ${success ? 'text-green-600' : 'text-destructive'}`}>
                            {error}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-4">
                        <Button type="submit" disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                'Submit'
                            )}
                        </Button>
                        <Button type="button" variant="secondary" onClick={closePopup}>
                            Cancel
                        </Button>
                    </div>
                </form>}
            </DialogContent>
        </Dialog>
    );
};

export default ReferralPopup;
